import { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { ClassListContext } from '@/features/class/classListContext';
import clsx from 'clsx';
import { useSidebarNavItems } from '@/app/navigation/usePrimaryNavItems';
import { isAppNavActive, isClassNavActive, classPath, APP_PATHS } from '@/app/routes/paths';
import { type ClassCatalogEntry } from '@/features/class/classCatalog';
import {
  useJoinedBoardsFromLogin,
  useOwnedBoards,
  type OwnedBoard,
} from '@/features/class/useOwnedBoards';
import { useJoinedClassesQuery } from '@/features/profile/useJoinedClassesQuery';
import { useUserProfileQuery } from '@/features/profile/useUserProfileQuery';
import { useClassManageStore } from '@/features/class/manage/classManageStore';
import { useShowReportCardMenu } from '@/features/report-cards/useShowReportCardMenu';
import { useAuthStore } from '@/store/authStore';
import {
  resolveUserTypeId,
  STUDENT_USER_TYPE_ID,
  PARENT_USER_TYPE_ID,
} from '@/features/create-post/composerModeAccess';
import { useCanAccessPayments } from '@/features/payments/usePaymentOptionAvailableOnWeb';
import { useTranslation } from 'react-i18next';
import { CrownIcon, PlusIcon } from '@/shared/icons';
import MyULogo from '@/shared/ui/MyULogo';
import { useSettingsStore } from '@/features/settings/settingsStore';
import { useMessageUnreadCount } from '@/features/messages/useMessageUnreadCount';
import {
  iconTouchTargetSquareClassName,
  navClassItemActiveClassName,
  navClassItemInactiveClassName,
  navItemActiveClassName,
  navItemInactiveClassName,
  primeButtonSmClassName,
  safeAreaPaddingBottomCompactClassName,
} from '@/shared/config/theme';

interface SidebarProps {
  className?: string;
  onNavigate?: () => void;
  onClassSelect?: (className: string, classCode: string) => void;
}

/** Palette used to colour the dot beside each dynamic owned class. */
const OWNED_CLASS_DOT_PALETTE = [
  'bg-[#f87171]',
  'bg-[#fb923c]',
  'bg-[#fbbf24]',
  'bg-[#34d399]',
  'bg-[#60a5fa]',
  'bg-[#c084fc]',
  'bg-[#f472b6]',
] as const;

function mapOwnedBoardsToClasses(boards: readonly OwnedBoard[]): ClassCatalogEntry[] {
  return boards.map((item, index) => ({
    // Route by boardId → /class/<boardId>/posts. The backend's
    // `feed/class?boardId=…` expects the numeric id.
    code: String(item.id),
    name: item.name,
    dotClassName: OWNED_CLASS_DOT_PALETTE[index % OWNED_CLASS_DOT_PALETTE.length],
  }));
}

/** Palette used for joined classes. */
const JOINED_CLASS_DOT_PALETTE = [
  'bg-[#60a5fa]',
  'bg-[#c084fc]',
  'bg-[#f472b6]',
  'bg-[#34d399]',
  'bg-[#fbbf24]',
  'bg-[#fb923c]',
  'bg-[#f87171]',
] as const;

interface JoinedBoardLike {
  id: number | string;
  name: string;
  code?: string;
}

function mapJoinedBoardsToClasses(
  boards: readonly JoinedBoardLike[] | undefined,
): ClassCatalogEntry[] {
  if (!boards || boards.length === 0) return [];
  return boards.map((item, index) => {
    const boardId = String(item.id);
    return {
      // Route by boardId → /class/<boardId>/posts.
      code: boardId,
      name: item.name,
      dotClassName: JOINED_CLASS_DOT_PALETTE[index % JOINED_CLASS_DOT_PALETTE.length],
    };
  });
}

function ClassRow({
  item,
  isActive,
  listContext,
  onSelect,
}: {
  item: ClassCatalogEntry;
  isActive: boolean;
  listContext: ClassListContext;
  onSelect?: (className: string, classCode: string, listContext: ClassListContext) => void;
}) {
  return (
    <button
      type="button"
      aria-current={isActive ? 'page' : undefined}
      onClick={() => onSelect?.(item.name, item.code, listContext)}
      className={clsx(
        'text-left',
        isActive ? navClassItemActiveClassName : navClassItemInactiveClassName,
      )}
    >
      <span className={clsx('h-3 w-3 shrink-0 rounded-full', item.dotClassName)} />
      <span className="line-clamp-2 min-w-0 break-words" title={item.name}>
        {item.name}
      </span>
    </button>
  );
}

export default function Sidebar({ className, onNavigate, onClassSelect }: SidebarProps) {
  const { t } = useTranslation('nav');
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const ownedBoards = useOwnedBoards();
  const ownedClasses = useMemo<readonly ClassCatalogEntry[]>(
    () => mapOwnedBoardsToClasses(ownedBoards),
    [ownedBoards],
  );

  const joinedFromLogin = useJoinedBoardsFromLogin();
  const authUserId = useAuthStore((state) => state.user?.id);
  const rawUser = useAuthStore((state) => state.rawUser);
  const authUser = useAuthStore((state) => state.user);
  const numericUserType = resolveUserTypeId(rawUser);
  const canCreateClass =
    numericUserType !== STUDENT_USER_TYPE_ID &&
    numericUserType !== PARENT_USER_TYPE_ID &&
    !(
      typeof authUser?.role === 'string' &&
      ['student', 'parent'].includes(authUser.role.trim().toLowerCase())
    );
  // Backed by GET /midl/api/v3/board/joined?userId=:userId — the same
  // endpoint the profile's "Joined classes" tab uses, so the sidebar list
  // stays in sync with the profile.
  const { items: joinedFromApi } = useJoinedClassesQuery(authUserId, { size: 50 });

  const ownedIdSet = useMemo(() => new Set(ownedBoards.map((b) => String(b.id))), [ownedBoards]);

  const joinedClasses = useMemo<readonly ClassCatalogEntry[]>(() => {
    // Prefer the live API list (authoritative); fall back to the login
    // payload while the request is in-flight or fails. Dedupe by id so
    // duplicates between sources don't appear twice, and drop any board
    // already shown under "Owned Classes".
    const merged: JoinedBoardLike[] = [];
    const seen = new Set<string>();
    const push = (b: JoinedBoardLike) => {
      const key = String(b.id);
      if (!key || seen.has(key)) return;
      if (ownedIdSet.has(key)) return;
      seen.add(key);
      merged.push(b);
    };
    (joinedFromApi ?? []).forEach((b) => push({ id: b.id, name: b.name, code: b.code }));
    joinedFromLogin.forEach((b) => push({ id: b.id, name: b.name, code: b.code }));
    return mapJoinedBoardsToClasses(merged);
  }, [joinedFromApi, joinedFromLogin, ownedIdSet]);
  const openCreateClass = useClassManageStore((state) => state.openCreateClass);
  const showPaymentsMenu = useCanAccessPayments();
  const showReportCardMenu = useShowReportCardMenu();
  const sidebarNavItems = useSidebarNavItems(showPaymentsMenu, showReportCardMenu);
  const messageUnreadCount = useMessageUnreadCount();

  // Prime activation on CTA click if user is not already prime
  const { data: profile } = useUserProfileQuery();
  const primeStored = useSettingsStore((state) => state.prime);
  const primeIsActive = (profile?.prime?.isActive ?? primeStored.isActive) === true;

  const handleClassSelect = (
    className: string,
    classCode: string,
    listContext: ClassListContext,
  ) => {
    if (onClassSelect) {
      onClassSelect(className, classCode);
    } else {
      navigate(classPath(classCode), { state: { classListContext: listContext } });
    }
    onNavigate?.();
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  const handlePrimeCtaClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (primeIsActive) {
      onNavigate?.();
      return; // allow default navigation
    }
    e.preventDefault();
    navigate(APP_PATHS.prime);
    onNavigate?.();
  };

  return (
    <aside
      className={clsx(
        'flex h-full w-64 flex-col border-e border-gray-300 bg-white shadow-xl lg:shadow-none',
        className,
      )}
    >
      {/* pl-8 = nav list p-4 + nav item px-4 — lines up with Home / Messages / Calendar icons */}
      <div className="flex min-h-16 shrink-0 items-center border-b border-gray-300 pl-8 pr-4">
        <MyULogo variant="full" size="sidebar" href={APP_PATHS.feed} />
      </div>

      <nav className="flex-1 overflow-y-auto" aria-label={t('mainNav')}>
        <div className="space-y-1 p-4">
          {sidebarNavItems.map((item) => {
            const { Icon, label, path } = item;
            const isActive = isAppNavActive(pathname, path);

            return (
              <button
                key={label}
                type="button"
                disabled={!path}
                aria-current={isActive ? 'page' : undefined}
                onClick={() => path && handleNavClick(path)}
                className={clsx(
                  isActive ? navItemActiveClassName : navItemInactiveClassName,
                  !path && 'cursor-default opacity-60',
                )}
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span>{label}</span>
                {path === APP_PATHS.messages && messageUnreadCount > 0 ? (
                  <span
                    className="ms-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-solid px-1.5 text-xs font-semibold tabular-nums text-white"
                    aria-label={`${messageUnreadCount} unread messages`}
                  >
                    {messageUnreadCount > 99 ? '99+' : messageUnreadCount}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        {(canCreateClass || ownedClasses.length > 0) && (
          <div className="px-4 py-3">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                {t('ownedClasses')}
              </h3>
              {canCreateClass && (
                <button
                  type="button"
                  className={iconTouchTargetSquareClassName}
                  title={t('addClass')}
                  aria-label={t('addClass')}
                  onClick={openCreateClass}
                >
                  <PlusIcon className="h-4 w-4 text-gray-600" />
                </button>
              )}
            </div>
            <div className="space-y-1">
              {ownedClasses.length > 0 ? (
                ownedClasses.map((classItem) => (
                  <ClassRow
                    key={`owned-${classItem.code}`}
                    item={classItem}
                    listContext="owned"
                    isActive={isClassNavActive(pathname, classItem.code)}
                    onSelect={handleClassSelect}
                  />
                ))
              ) : (
                <p className="px-4 py-2 text-xs text-gray-500">{t('noOwnedClasses')}</p>
              )}
            </div>
          </div>
        )}

        {(canCreateClass || joinedClasses.length > 0) && (
          <div className="px-4 py-3">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              {t('joinedClasses')}
            </h3>
            <div className="space-y-1">
              {joinedClasses.length > 0 ? (
                joinedClasses.map((classItem) => (
                  <ClassRow
                    key={`joined-${classItem.code}`}
                    item={classItem}
                    listContext="joined"
                    isActive={isClassNavActive(pathname, classItem.code)}
                    onSelect={handleClassSelect}
                  />
                ))
              ) : (
                <p className="px-4 py-2 text-xs text-gray-500">{t('noJoinedClasses')}</p>
              )}
            </div>
          </div>
        )}
      </nav>

      <div
        className={`space-y-2 border-t border-gray-300 px-4 pt-4 ${safeAreaPaddingBottomCompactClassName}`}
      >
        <Link to={APP_PATHS.prime} onClick={handlePrimeCtaClick} className={primeButtonSmClassName}>
          <CrownIcon className="h-3.5 w-3.5" />
          {t('tryPrime')}
        </Link>
      </div>
    </aside>
  );
}
